import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';
import { renderToString } from 'react-dom/server';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import {
  eligibleShippingBoxListing,
  shippingGuideDetailsForListing,
  shippingBoxIdsForListing,
  LISTING_ID_FOR_1_GUITAR_OR_KEYBOARD_BOX,
  LISTING_ID_FOR_3_PACK_OF_GUITAR_OR_KEYBOARD_BOXES,
  LISTING_ID_FOR_GUITAR_OR_KEYBOARD_SHIPPING_KIT,
  LISTING_ID_FOR_5_PACK_OF_PEDAL_BOXES,
  LISTING_ID_FOR_10_PACK_OF_PEDAL_BOXES,
  LISTING_ID_FOR_MULTI_DEPTH_BOXES,
} from './item2_shipping_prompt_helpers';

import Item2ShippingPromptBoxListing from './item2_shipping_prompt_box_listing';
import { Listing, CoreListingsSearchShippingPrompt } from '../schemas/rql';

import GENERIC_SHIPPING_BOX_SVG from '../../images/shipping/shipping-box.svg';

const SHIPPING_REGION_CODES = ['US_CON'];
const REVERB_SHIPPING_BOX_LISTING_STATUSES = ['live'];

function trackShippingGuideClick(listingId: string) {
  trackEvent({
    eventName: MParticleEventName.ClickedReverbShippingGuideInsideShippingPrompt,
    listingId,
  });
}

function renderLinkToShippingGuide(shippingGuideDetails) {
  const link = shippingGuideDetails.guideLink;
  const linkText = I18n.t(`discovery.item2ShippingPrompt.${shippingGuideDetails.i18ItemKey}`);

  return renderToString(
    <CoreLink
      target="_blank"
      to={link}
    >
      {linkText}
    </CoreLink>,
  );
}

function renderShippingGuideText({ shippingBoxListing, shippingGuideDetails }) {
  const shippingGuideLink = renderLinkToShippingGuide(shippingGuideDetails);

  if (shippingBoxListing) {
    return I18n.t('discovery.item2ShippingPrompt.shippingGuideWhenBoxAvailable', {
      itemsLink: shippingGuideLink,
    });
  }
  return I18n.t('discovery.item2ShippingPrompt.shippingGuideWhenBoxUnavailable', {
    itemsLink: shippingGuideLink,
  });

}

interface IExternalProps {
  listing: Listing;
  overrideAddToCartRedirect?: boolean;
}

type IProps = ChildProps<IExternalProps, CoreListingsSearchShippingPrompt.Query>;

export function Item2ShippingPrompt({
  data,
  listing,
  overrideAddToCartRedirect,
}: IProps) {
  const { loading, listingsSearch } = data;

  const shippingGuideDetails = shippingGuideDetailsForListing(listing);

  const shippingBoxListing = eligibleShippingBoxListing({
    listingIdFilters: shippingBoxIdsForListing(listing) || [],
    shippingBoxListings: listingsSearch?.listings ?? [],
  });

  const sellersListingPath = overrideAddToCartRedirect ? `/item/${listing.id}-${listing.slug}` : '';

  useViewTracking({
    componentName: 'Item2ShippingPrompt',
    eventName: MParticleEventName.ComponentView,
    listingId: listing.id,
  }, !loading);

  if (loading) return null;

  return (
    <>
      <div className="g-container d-flex">
        {!shippingBoxListing && (
          <img
            alt={I18n.t('discovery.item2ShippingPrompt.shippingBox')}
            className="g-col-4"
            src={GENERIC_SHIPPING_BOX_SVG}
          />
        )}

        <div className="g-col-8 fx-grow">
          <h3 className="size-120 weight-bold mb-2">
            {I18n.t('discovery.item2ShippingPrompt.getReadyToPackYourItem')}
          </h3>

          <div className="mb-2" onClick={() => trackShippingGuideClick(listing.id)}>
            <SanitizedRender
              html={renderShippingGuideText({
                shippingBoxListing,
                shippingGuideDetails,
              })}
            />
          </div>
        </div>
      </div>

      {shippingBoxListing && (
        <Item2ShippingPromptBoxListing
          addToCartRedirectPath={sellersListingPath}
          shippingBoxListing={shippingBoxListing}
        />
      )}
    </>
  );
}

const withQuery = withGraphql<IProps, CoreListingsSearchShippingPrompt.Query>(
  gql`
    query Core_ListingsSearch_ShippingPrompt(
      $ids: [String]
      $shippingRegionCodes: [String]
      $statuses: [String]
    ){
      listingsSearch(input: {
       ids: $ids,
       shippingRegionCodes: $shippingRegionCodes
       statuses: $statuses
      }) {
        listings {
          _id
          id
          title
          slug
          state
          price {
            amountCents
            display
          }
          images(input: {
            transform: "card_square",
            count: 1
          }) {
            source
          }
        }
      }
    }
  `,
  {
    options: {
      errorPolicy: 'all',
      ssr: false,
      variables: {
        ids: [
          LISTING_ID_FOR_GUITAR_OR_KEYBOARD_SHIPPING_KIT,
          LISTING_ID_FOR_1_GUITAR_OR_KEYBOARD_BOX,
          LISTING_ID_FOR_3_PACK_OF_GUITAR_OR_KEYBOARD_BOXES,
          LISTING_ID_FOR_5_PACK_OF_PEDAL_BOXES,
          LISTING_ID_FOR_10_PACK_OF_PEDAL_BOXES,
          LISTING_ID_FOR_MULTI_DEPTH_BOXES,
        ],
        shippingRegionCodes: SHIPPING_REGION_CODES,
        statuses: REVERB_SHIPPING_BOX_LISTING_STATUSES,
      },
    },
  },
);

export default withQuery(Item2ShippingPrompt);
